import { isEmpty } from 'lodash-es';
import { R as tabbable, C as BXF_FOCUS_KEYBOARD_ONLY, A as BXF_MOUSE_FOCUS } from './bxf-utils-3908c3c5.js';
import { a as querySelectorDeep } from './query-selector-shadow-dom-dc946919.js';

class BxfPositionConstants {
    static STRING = {
        OVERLAY_SOURCE: 'bxf-overlay__source',
        OVERLAY_CONTAINER: 'bxf-overlay__container',
        DROPDOWN_CONTAINER: 'bxf-overlay__dropdown-container',
        MENU_CONTAINER: 'bxf-overlay__menu-container',
        POPOVER_CONTAINER: 'bxf-overlay__popover-container',
        MODAL_CONTAINER: 'bxf-overlay__modal-container',
        CALENDAR_CONTAINER: 'bxf-calendar__container',
        TODAY_MENU_CONTAINER: 'bxf-calendar__today_menu',
        SETTINGS_CONTAINER: 'bxf-calendar-settings__container',
        BACKDROP_CONTAINER: 'bxf-overlay__backdrop-container',
        BACKDROP_CENTER: 'bxf-overlay__backdrop--center-position',
        BACKDROP_SHOW: 'bxf-overlay__backdrop--showing',
        BACKDROP_SHOW_NESTED: 'bxf-overlay__backdrop-nested--showing',
        CENTER_POSITION_MODAL: '.bxf-overlay__backdrop--center-position > .bxf-modal',
        GRID_POPUP: 'ag-popup',
        DIV: 'div',
        BOTTOM_PADDING: 10,
        HIDDEN: 'hidden',
        TARGET_EVENT_TYPE: 'targetEventType',
        PARENT_CLASS_NAME: 'parentElement.className',
        PATH: 'path',
        CLASSNAME: 'className',
        STYLE: 'style',
        VISIBLE: 'visible',
        PARENT: 'parentElement',
        NONE: 'none',
        SCRIPT: 'script',
        NOSCRIPT: 'noscript',
        DATA_BXF_ARIA_HIDDEN: 'data-bxf-aria-hidden',
        POPOVER_TIP_OFFSET: 25,
        DUMMY_ELEM: 'bxf-overlay__dummy',
        INITIAL_ELEMENT_HEIGHT: 0,
    };
}

class FocusTrap {
    /**
     * Convert ally.js trap context to a DOM element or document
     * 	This is needed to migrate from ally.js to tabbable.
     * @param trapContext trap context used for ally.js
     * @returns DOM element or document
     */
    static _getTrapContext(trapContext) {
        if (typeof trapContext === 'string') {
            return trapContext === 'document'
                ? document
                : querySelectorDeep(trapContext);
        }
        return trapContext;
    }
    /**
     * Get all focusable elements inside a given CSS selector or element
     * @static
     * @param {string | HTMLElement | SVGElement} trapContext
     * @returns {Array<HTMLElement | SVGElement>}
     */
    static getFocusableElems(trapContext) {
        const trapEl = this._getTrapContext(trapContext);
        // get tabbable elements inside trapEl
        // tabbable is a superset of focusable elements
        // if trapEl is document, use documentElement, otherwise use trapEl
        return tabbable(trapEl instanceof Document ? trapEl.documentElement : trapEl, { getShadowRoot: true });
    }
    /**
     * Get document from element, default to global document
     * @param el  Element
     * @returns Document
     */
    static _getDocument(el) {
        if (!el) {
            return document;
        }
        if (el.nodeType === Node.DOCUMENT_NODE) {
            return el;
        }
        return el.ownerDocument ?? document;
    }
    /**
     * Retrieve focused element within context of a CSS selector or element, accounting for shadow root
     * @static
     * @param {string | HTMLElement | SVGElement} trapContext
     * @returns {HTMLElement}
     */
    static getFocusedElem(trapContext) {
        const el = this._getTrapContext(trapContext);
        if (!el)
            return null;
        const documentContext = el instanceof Document ? el : this._getDocument(el);
        let activeElem = documentContext.activeElement;
        while (activeElem.shadowRoot && activeElem.shadowRoot.activeElement) {
            activeElem = activeElem.shadowRoot.activeElement;
        }
        return activeElem;
    }
    /**
     * Toggle mouse focus to on/off for a given element.
     * @static
     * @param {Element} elem
     * @param {boolean} mouseFocus
     */
    static toggleMouseFocus(elem, mouseFocus) {
        /**
         * Skip adding BXF_MOUSE_FOCUS if a target exists with BXF_FOCUS_KEYBOARD_ONLY
         * 	to smoothen transition to :focus-visible selector
         * @see ADO-1343083
         */
        if (!elem?.classList?.contains(BXF_FOCUS_KEYBOARD_ONLY)) {
            if (mouseFocus && !elem.classList.contains(BXF_MOUSE_FOCUS)) {
                elem.classList.add(BXF_MOUSE_FOCUS);
            }
            else if (!mouseFocus &&
                elem.classList.contains(BXF_MOUSE_FOCUS)) {
                elem.classList.remove(BXF_MOUSE_FOCUS);
            }
        }
    }
    /**
     * Get the first tabbable element in context of a given selector or element
     * @param trapContext trap context used for ally.js
     * @returns first tabbable element if it exists, otherwise null
     */
    static firstTabbable(trapContext) {
        const tabbableElems = this.getFocusableElems(trapContext);
        return tabbableElems ? tabbableElems[0] : null;
    }
    /**
     * Get the last tabbable element in context of a given selector or element
     * @param trapContext trap context used for ally.js
     * @returns last tabbable element if it exists, otherwise null
     */
    static lastTabbable(trapContext) {
        const tabbableElems = this.getFocusableElems(trapContext);
        return tabbableElems ? tabbableElems[tabbableElems.length - 1] : null;
    }
    static _moveFocusIntoElem(trapContext, elem, isMouseFocus) {
        let elemToFocus;
        if (elem) {
            elemToFocus = elem;
        }
        else {
            const rootElem = typeof trapContext === 'string'
                ? querySelectorDeep(trapContext)
                : trapContext;
            rootElem.tabindex = 0;
            elemToFocus = rootElem;
        }
        this.toggleMouseFocus(elemToFocus, isMouseFocus);
        elemToFocus.focus();
    }
    /**
     * Focus the first focusable element in context of a given selector or element
     * @static
     * @param {string | HTMLElement | SVGElement} trapContext
     * @param {boolean} isMouseFocus
     * @returns {void}
     */
    static focusFirstElem(trapContext, isMouseFocus) {
        const firstFocusableElem = this.firstTabbable(trapContext);
        this._moveFocusIntoElem(trapContext, firstFocusableElem, isMouseFocus);
    }
    /**
     * Focus the last focusable element in context of a given selector or element
     * @static
     * @param {string | HTMLElement | SVGElement} trapContext
     * @param {boolean} isMouseFocus
     * @returns {void}
     */
    static focusLastElem(trapContext, isMouseFocus) {
        const lastFocusableElem = this.lastTabbable(trapContext);
        this._moveFocusIntoElem(trapContext, lastFocusableElem, isMouseFocus);
    }
    /**
     * Trap keyboard focus in context of a given css selector or element.
     */
    static trapFocus(event, trapContext) {
        event.stopPropagation(); // prevent ally.query from being called more than once in nested overlays
        const focusedElem = this.getFocusedElem(trapContext);
        const focusableElems = this.getFocusableElems(trapContext);
        const trapElem = typeof trapContext === 'string'
            ? document.querySelector(trapContext)
            : trapContext;
        if (focusableElems && !isEmpty(focusableElems)) {
            const firstFocusableElem = focusableElems[0];
            const lastFocusableElem = focusableElems[focusableElems.length - 1];
            let elemToFocus;
            if (focusedElem) {
                // check for shadow root in case of BXF-button
                if (event.shiftKey &&
                    (focusedElem === firstFocusableElem ||
                        focusedElem === trapElem)) {
                    // change focus to last focusable elem
                    elemToFocus = lastFocusableElem;
                    event.preventDefault();
                }
                else if (!event.shiftKey &&
                    focusedElem === lastFocusableElem) {
                    // change focus to first focusable elem
                    elemToFocus = firstFocusableElem;
                    event.preventDefault();
                }
                else {
                    elemToFocus = focusedElem;
                }
            }
            else {
                // change focus to first focusable elem
                event.preventDefault();
                elemToFocus = firstFocusableElem;
            }
            this.toggleMouseFocus(elemToFocus, false);
            elemToFocus.focus();
        }
        else if (isEmpty(focusableElems)) {
            // do not allow user to change focus until Esc pressed
            event.preventDefault();
        }
    }
}

export { BxfPositionConstants as B, FocusTrap as F };
